import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWandMagicSparkles,
  faSpinner,
  faXmark,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import instance from "../apiInstance";
import "./PromoGenieModal.css";

const COLORS = ["Preta", "Bege", "Marrom", "Branca", "Vermelha", "Rosa", "Azul", "Dourada"];
const ITEMS = [
  "Bolsa",
  "Cinto",
  "Carteira",
  "Sapato",
  "Relógio",
  "Óculos",
  "Joia",
  "Mochila",
  "Lenço",
  "Roupa",
];
const BUDGETS = [
  { label: "Até 1k", value: 1000 },
  { label: "Até 3k", value: 3000 },
  { label: "Até 5k", value: 5000 },
  { label: "Até 10k", value: 10000 },
  { label: "Até 15k", value: 15000 },
  { label: "Até 20k", value: 20000 },
  { label: "Até 30k", value: 30000 },
  { label: "Até 40k", value: 40000 },
  { label: "Até 50k", value: 50000 },
  { label: "Até 70k", value: 70000 },
  { label: "Até 90k", value: 90000 },
  { label: "Até 100k", value: 100000 },
];
const SIZES = {
  Bolsa: ["Mini", "Small", "Média", "Grande"],
  Cinto: ["Mini", "Small", "Média", "Grande"],
  Carteira: ["Mini", "Small", "Média", "Grande"],
  Mochila: ["Mini", "Small", "Média", "Grande"],
  Joia: ["Mini", "Small", "Média", "Grande"],
  Lenço: ["Mini", "Small", "Média", "Grande"],
  Roupa: ["Mini", "Small", "Média", "Grande", "P", "M", "G", "GG"],
  Vestuário: ["Mini", "Small", "Média", "Grande", "P", "M", "G", "GG"],
  Óculos: ["Mini", "Small", "Média", "Grande"],
  Relógio: ["Mini", "Small", "Média", "Grande"],
  Sapato: ["Mini", "Small", "Média", "Grande", "35", "36", "37", "38", "39", "40"],
};
const DEFAULT_SIZES = ["Mini", "Small", "Média", "Grande"];

const VISIT_KEY = "promo_genie_visits";
const SKIP_KEY = "promo_genie_skipped";
const POPUP_EVERY = 3;

export function shouldAutoOpenGenie() {
  try {
    if (sessionStorage.getItem(SKIP_KEY)) return false;
    const n = Number(localStorage.getItem(VISIT_KEY) || 0) + 1;
    localStorage.setItem(VISIT_KEY, String(n));
    return n >= 2 && n % POPUP_EVERY === 2;
  } catch {
    return false;
  }
}

const emptyItem = (category = "") => ({ category, model: "", color: "", size: "" });

export default function PromoGenieModal({
  open,
  onClose,
  onApplied,
  destination = "/dashboard",
  wishExpired = false,   // true → mostra mensagem "sua busca expirou"
}) {
  const [step, setStep] = useState("budget");
  const [budget, setBudget] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [refiningIndex, setRefiningIndex] = useState(0);
  const [refinedItems, setRefinedItems] = useState([]);
  const [current, setCurrent] = useState(emptyItem());
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");

  const resetAll = useCallback(() => {
    setStep("budget");
    setBudget(null);
    setSelectedCategories([]);
    setRefiningIndex(0);
    setRefinedItems([]);
    setCurrent(emptyItem());
    setError("");
    setSearching(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    instance
      .post("/promos/wish/audit", { action: "open", surface: destination })
      .catch(() => {});
  }, [open, destination]);

  const handleClose = useCallback(() => {
    try {
      sessionStorage.setItem(SKIP_KEY, "1");
    } catch {}
    resetAll();
    onClose?.();
  }, [onClose, resetAll]);

  const finishAndSave = useCallback(
    async (allItems) => {
      const packed = (allItems || []).filter(
        (it) => it.category || it.model || it.color || it.size
      );
      if (!packed.length && budget == null) {
        setError("Escolha pelo menos o orçamento ou uma peça.");
        return;
      }
      setError("");
      setLoading(true);
      try {
        await instance.put("/promos/wish", {
          budget_today: budget,
          max_price: budget,
          items: packed.map((it) => ({
            category: it.category,
            model: it.model,
            query: [it.category, it.model].filter(Boolean).join(" "),
            color: it.color || null,
            size: it.size || null,
          })),
        });
        setSearching(true);
        await new Promise((r) => setTimeout(r, 1400));
        resetAll();
        onApplied?.({ destination });
      } catch {
        setError("Não consegui salvar agora. Tente novamente.");
        setSearching(false);
      } finally {
        setLoading(false);
      }
    },
    [budget, destination, onApplied, resetAll]
  );

  const toggleCategory = useCallback((item) => {
    setError("");
    setSelectedCategories((prev) => {
      if (prev.includes(item)) {
        return prev.filter((x) => x !== item);
      }
      if (prev.length >= 4) {
        setError("Você pode selecionar no máximo 4 peças.");
        return prev;
      }
      return [...prev, item];
    });
  }, []);

  const startRefining = useCallback(() => {
    if (!selectedCategories.length) {
      setError("Selecione pelo menos 1 peça.");
      return;
    }
    setError("");
    setRefiningIndex(0);
    setRefinedItems([]);
    setCurrent(emptyItem(selectedCategories[0]));
    setStep("model");
  }, [selectedCategories]);

  const quickSearchCategories = useCallback(() => {
    if (!selectedCategories.length) {
      setError("Selecione pelo menos 1 peça.");
      return;
    }
    const directItems = selectedCategories.map((cat) => ({
      category: cat,
      model: "",
      color: "",
      size: "",
    }));
    finishAndSave(directItems);
  }, [finishAndSave, selectedCategories]);

  const currentCategory = selectedCategories[refiningIndex] || current.category || "peça";

  const advanceFromSize = useCallback(
    (chosenSize) => {
      const finishedPiece = {
        category: currentCategory,
        model: current.model || "",
        color: current.color || "",
        size: chosenSize || current.size || "",
      };
      const nextRefined = [...refinedItems, finishedPiece];
      setRefinedItems(nextRefined);

      if (refiningIndex + 1 < selectedCategories.length) {
        const nextIdx = refiningIndex + 1;
        setRefiningIndex(nextIdx);
        setCurrent(emptyItem(selectedCategories[nextIdx]));
        setStep("model");
      } else {
        finishAndSave(nextRefined);
      }
    },
    [current, currentCategory, finishAndSave, refinedItems, refiningIndex, selectedCategories]
  );

  const saveAndSearchNow = useCallback(() => {
    const currentPiece = {
      category: currentCategory,
      model: current.model || "",
      color: current.color || "",
      size: current.size || "",
    };
    const remaining = selectedCategories.slice(refiningIndex + 1).map((c) => ({
      category: c,
      model: "",
      color: "",
      size: "",
    }));
    const all = [...refinedItems, currentPiece, ...remaining];
    finishAndSave(all);
  }, [current, currentCategory, finishAndSave, refinedItems, refiningIndex, selectedCategories]);

  if (!open) return null;

  if (searching) {
    return (
      <div className="genie-overlay">
        <div className="genie-modal genie-modal--searching">
          <FontAwesomeIcon icon={faSpinner} spin className="genie-search-spin" />
          <h2>Buscando ofertas especiais para você ✨</h2>
          <p>
            Vou priorizar as peças e tamanhos que você selecionou no seu feed.
          </p>
        </div>
      </div>
    );
  }

  const sizeOpts = SIZES[currentCategory] || DEFAULT_SIZES;

  const titles = {
    budget: wishExpired
      ? "Sua busca expirou 🕰️ Quer investir até quanto hoje?"
      : "Quer investir até quanto hoje?",
    category: "O que você procura?",
    model: selectedCategories.length > 1
      ? `Qual modelo de ${currentCategory}? (${refiningIndex + 1} de ${selectedCategories.length})`
      : `Qual modelo de ${currentCategory}?`,
    color: selectedCategories.length > 1
      ? `Qual cor de ${currentCategory}? (${refiningIndex + 1} de ${selectedCategories.length})`
      : `Qual cor de ${currentCategory}?`,
    size: selectedCategories.length > 1
      ? `Qual tamanho de ${currentCategory}? (${refiningIndex + 1} de ${selectedCategories.length})`
      : `Qual tamanho de ${currentCategory}?`,
  };

  const subs = {
    budget: "Pode ser um teto solto. Depois a gente afina peça por peça.",
    category: "Selecione de 1 até 4 opções que gostaria de ver.",
    model: "Neverfull, Speedy, 574… ou pule se ainda não souber.",
    color: "A que você mais gosta ou tem em mente hoje.",
    size: "Se não souber, pode pular.",
  };

  return (
    <div className="genie-overlay">
      <div className="genie-modal">
        <button type="button" className="genie-close" onClick={handleClose} aria-label="Fechar">
          <FontAwesomeIcon icon={faXmark} />
        </button>
        <div className="genie-icon">
          <FontAwesomeIcon icon={faWandMagicSparkles} />
        </div>
        <div className="genie-progress">
          {["budget", "category", "model", "color", "size"].map((s) => (
            <span key={s} className={s === step ? "on" : ""} />
          ))}
        </div>
        <h2>{titles[step] || "Personalize suas ofertas"}</h2>
        <p className="genie-sub">{subs[step]}</p>

        {step === "budget" && (
          <div className="genie-chips genie-chips--budget">
            {BUDGETS.map((b) => (
              <button
                type="button"
                key={b.label}
                className={budget === b.value ? "on" : ""}
                onClick={() => {
                  setBudget(b.value);
                  setStep("category");
                }}
              >
                {b.label}
              </button>
            ))}
          </div>
        )}

        {step === "category" && (
          <div>
            <div className="genie-chips">
              {ITEMS.map((item) => {
                const isSelected = selectedCategories.includes(item);
                return (
                  <button
                    type="button"
                    key={item}
                    className={isSelected ? "on" : ""}
                    onClick={() => toggleCategory(item)}
                  >
                    {isSelected && <FontAwesomeIcon icon={faCheck} style={{ marginRight: 6 }} />}
                    {item}
                  </button>
                );
              })}
            </div>

            {selectedCategories.length > 0 && (
              <div style={{ marginTop: "18px", display: "flex", flexDirection: "column", gap: "10px" }}>
                <button
                  type="button"
                  className="genie-submit"
                  onClick={startRefining}
                >
                  Continuar ({selectedCategories.length} {selectedCategories.length === 1 ? "peça" : "peças"})
                </button>
                <button
                  type="button"
                  className="genie-submit genie-submit--ghost"
                  disabled={loading}
                  onClick={quickSearchCategories}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Salvando…
                    </>
                  ) : (
                    "Pronto, buscar ofertas agora"
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {step === "model" && (
          <form
            className="genie-form"
            onSubmit={(e) => {
              e.preventDefault();
              setStep("color");
            }}
          >
            <input
              value={current.model}
              onChange={(e) => setCurrent((c) => ({ ...c, model: e.target.value }))}
              placeholder={`Ex: Neverfull, Speedy, flap…`}
              autoFocus
            />
            <button type="submit" className="genie-submit">
              Continuar
            </button>
            <button type="button" className="genie-later" onClick={() => setStep("color")}>
              Ainda não sei o modelo
            </button>
            <button
              type="button"
              className="genie-later"
              style={{ color: "#b8962e", fontWeight: 700, marginTop: "8px" }}
              onClick={saveAndSearchNow}
            >
              Pronto, buscar ofertas agora →
            </button>
          </form>
        )}

        {step === "color" && (
          <div>
            <div className="genie-chips">
              {COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  className={current.color === c ? "on" : ""}
                  onClick={() => {
                    setCurrent((cur) => ({ ...cur, color: c }));
                    setStep("size");
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
            <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "6px" }}>
              <button type="button" className="genie-later" onClick={() => setStep("size")}>
                Qualquer cor
              </button>
              <button
                type="button"
                className="genie-later"
                style={{ color: "#b8962e", fontWeight: 700, marginTop: "4px" }}
                onClick={saveAndSearchNow}
              >
                Pronto, buscar ofertas agora →
              </button>
            </div>
          </div>
        )}

        {step === "size" && (
          <div>
            <div className="genie-chips">
              {sizeOpts.map((s) => (
                <button
                  type="button"
                  key={s}
                  className={current.size === s ? "on" : ""}
                  onClick={() => advanceFromSize(s)}
                >
                  {s}
                </button>
              ))}
            </div>
            <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "6px" }}>
              <button type="button" className="genie-later" onClick={() => advanceFromSize("")}>
                Não sei o tamanho
              </button>
              <button
                type="button"
                className="genie-later"
                style={{ color: "#b8962e", fontWeight: 700, marginTop: "4px" }}
                onClick={saveAndSearchNow}
              >
                Pronto, buscar ofertas agora →
              </button>
            </div>
          </div>
        )}

        {error && <p className="genie-error" style={{ marginTop: "12px" }}>{error}</p>}
      </div>
    </div>
  );
}
